import { computed, type ComputedRef, type Ref, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import useCurrencies from '../composables/use-currencies'
import { useFormatting } from '../composables/use-formatting'
import { type LocationBasedFactorType, useLocationBasedFactorTypes } from '../composables/use-location-based-factor-types'
import { GET_CUSTOM_EMISSION_FACTOR_STATUS, GET_PORTFOLIO } from '../helpers/apiURLS'
import getApiData, { fetchApiData } from '../helpers/getApiData'
import { isBootstrapActive } from '../pages/collection/asset/bootstrap-state'
import type { PortfolioApiData } from '../pages/portfolio/settings/types'
import type { Currency } from '../pages/roadmap/components/types'
import { useBreadcrumbsStore } from './breadcrumbsStore'

type CurrentPortfolioState = {
    isLoading: Ref<boolean>
    data: Ref<PortfolioApiData | null>
    currency: ComputedRef<Currency | null>
    locationBasedFactorType: ComputedRef<LocationBasedFactorType | null>
    isCustomLocationFactorsActive: Ref<boolean>
    isScalerScoresEnabled: Ref<boolean>
    numberFormatter: (number: number) => string
    refresh: () => Promise<void>
    preloadLbEmissionFactorsStatus: (active: boolean) => void
}

export const useCurrentPortfolioStore = defineStore('currentPortfolioStore', (): CurrentPortfolioState => {
    const breadcrumbsStore = useBreadcrumbsStore()
    const { formatNumber } = useFormatting()

    const { currencies } = useCurrencies()
    const locationBasedFactorTypes = useLocationBasedFactorTypes()

    const data = ref<PortfolioApiData | null>(null)
    const isLoading = ref(false)
    const isCustomLocationFactorsActive = ref(false)

    const locationBasedFactorType = computed<LocationBasedFactorType | null>(() => {
        return locationBasedFactorTypes.value.find(type => type.id === data.value?.emission_factor_type_id) || null
    })

    const isScalerScoresEnabled = computed<boolean>(() => {
        return data.value?.scaler_scores_enabled ?? false
    })

    const currency = computed<Currency | null>(() => {
        return currencies.value.find(currency => currency.id === data.value?.currency_id) || null
    })

    async function fetchPortfolio(portfolioId: number) {
        // Skip if already loaded for this portfolio
        if (data.value?.id === portfolioId && !isLoading.value) {
            return
        }
        data.value = null
        isLoading.value = true
        const response = await getApiData(GET_PORTFOLIO(portfolioId))
        const frameworks = response.data.data.frameworks || {}

        Object.keys(frameworks).forEach((key) => {
            if (typeof frameworks[key].due_date !== 'string') {
                delete frameworks[key].due_date
            }
        })
        data.value = {
            ...response.data.data,
            frameworks: frameworks,
        }
        isLoading.value = false
    }

    // When true, fetchCustomEmissionFactorsStatus defers — bootstrap will provide the data
    let _lbStatusPreloaded = false

    async function fetchCustomEmissionFactorsStatus(portfolioId: number) {
        if (_lbStatusPreloaded) {
            return
        }
        isCustomLocationFactorsActive.value = false
        const response = await fetchApiData<{ data: { custom_location_factors_active: boolean } }>(
            GET_CUSTOM_EMISSION_FACTOR_STATUS(portfolioId),
        )
        isCustomLocationFactorsActive.value = response.data?.data.custom_location_factors_active ?? false
    }

    function preloadLbEmissionFactorsStatus(active: boolean) {
        isCustomLocationFactorsActive.value = active
        _lbStatusPreloaded = true
    }

    const INITIAL_DEFER_MS = 3000

    watch(() => breadcrumbsStore.portfolioId, (portfolioId, oldPortfolioId) => {
        if (portfolioId) {
            if (oldPortfolioId != null) {
                // Real portfolio change — fetch immediately
                fetchPortfolio(portfolioId)
                _lbStatusPreloaded = false
                fetchCustomEmissionFactorsStatus(portfolioId)
            } else if (isBootstrapActive()) {
                // Initial mount with bootstrap — defer to reduce FPM contention.
                // Input portal uses inputPortalStore for portfolio data, not this store.
                setTimeout(() => fetchPortfolio(portfolioId), INITIAL_DEFER_MS)
                setTimeout(() => {
                    if (!_lbStatusPreloaded) {
                        fetchCustomEmissionFactorsStatus(portfolioId)
                    }
                }, INITIAL_DEFER_MS)
            } else {
                // No bootstrap — fetch immediately (original behavior)
                fetchPortfolio(portfolioId)
                fetchCustomEmissionFactorsStatus(portfolioId)
            }
        }
    }, { immediate: true })

    function numberFormatter(number: number): string {
        return formatNumber({ number, unitSystem: data.value?.default_unit })
    }

    async function refresh() {
        if (!breadcrumbsStore.portfolioId) {
            return
        }
        await fetchPortfolio(breadcrumbsStore.portfolioId)
    }

    return { data, isLoading, isCustomLocationFactorsActive, currency, locationBasedFactorType, isScalerScoresEnabled, numberFormatter, refresh, preloadLbEmissionFactorsStatus }
})
